import { useEffect, useState } from "react";

import { startOfDay } from "~/lib/calendar";
import { addDays } from "~/lib/routine";

/**
 * The member's local today, kept current across midnight.
 *
 * A phone app is resumed, not reloaded: one opened at 22:00 to tick the evening dose
 * and brought back at 7:00 would otherwise still call yesterday "today", show both of
 * its doses as done and tick the morning into the wrong day. So the value is re-read
 * when the page becomes visible again and when a timer reaches the next midnight — a
 * suspended app fires an overdue timer as soon as it wakes, which covers the rest.
 */
export function useToday(): Date {
  const [today, setToday] = useState(() => startOfDay(new Date()));

  useEffect(() => {
    const refresh = () => {
      const now = startOfDay(new Date());
      setToday((current) =>
        current.getTime() === now.getTime() ? current : now,
      );
    };
    // A second past midnight, so the timer can never fire on the old day's last tick.
    const untilTomorrow = addDays(today, 1).getTime() - Date.now() + 1000;
    const timer = window.setTimeout(refresh, Math.max(untilTomorrow, 0));
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [today]);

  return today;
}
